import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, Sparkles } from 'lucide-react';
import { Button, Card, CardContent, Input, Label, Badge, Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, useToast } from '@mawkaa/ui';
import { api } from '@/lib/api';
import { useSitesStore } from '@/stores/sitesStore';
import { cn } from '@mawkaa/ui';

interface Template {
  id: string;
  name: string;
  nameAr?: string;
  description: string;
  category: string;
  thumbnail: string;
  isPremium: boolean;
}

interface Category {
  id: string;
  name: string;
  nameAr: string;
  icon: string;
}

const PLACEHOLDER_TEMPLATES: Template[] = [
  {
    id: 'tpl_restaurant_1',
    name: 'Restaurant Classique',
    description: 'Parfait pour les restaurants et cafés',
    category: 'restaurant',
    thumbnail: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=400&h=300&fit=crop',
    isPremium: false,
  },
  {
    id: 'tpl_beauty_1',
    name: 'Salon de Beauté',
    description: 'Idéal pour les salons et spas',
    category: 'beauty',
    thumbnail: 'https://images.unsplash.com/photo-1560066984-138dadb4c035?w=400&h=300&fit=crop',
    isPremium: false,
  },
  {
    id: 'tpl_commerce_1',
    name: 'Boutique Moderne',
    description: 'Pour les boutiques et commerces',
    category: 'commerce',
    thumbnail: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400&h=300&fit=crop',
    isPremium: false,
  },
  {
    id: 'tpl_services_1',
    name: 'Services Pro',
    description: 'Pour les prestataires de services',
    category: 'services',
    thumbnail: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=400&h=300&fit=crop',
    isPremium: false,
  },
  {
    id: 'tpl_portfolio_1',
    name: 'Portfolio Créatif',
    description: 'Présentez vos travaux',
    category: 'portfolio',
    thumbnail: 'https://images.unsplash.com/photo-1561070791-2526d30994b5?w=400&h=300&fit=crop',
    isPremium: true,
  },
  {
    id: 'tpl_landing_1',
    name: 'Landing Page',
    description: 'Page de destination efficace',
    category: 'landing',
    thumbnail: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=300&fit=crop',
    isPremium: false,
  },
];

const CATEGORIES: Category[] = [
  { id: 'all', name: 'Tous', nameAr: 'الكل', icon: 'grid' },
  { id: 'restaurant', name: 'Restaurant', nameAr: 'مطعم', icon: 'utensils' },
  { id: 'beauty', name: 'Beauté', nameAr: 'تجميل', icon: 'sparkles' },
  { id: 'commerce', name: 'Commerce', nameAr: 'تجارة', icon: 'shopping-bag' },
  { id: 'services', name: 'Services', nameAr: 'خدمات', icon: 'briefcase' },
  { id: 'portfolio', name: 'Portfolio', nameAr: 'معرض', icon: 'image' },
  { id: 'landing', name: 'Landing', nameAr: 'هبوط', icon: 'layout' },
];

export function TemplatesPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { createSite, checkSubdomain } = useSitesStore();

  const [templates, setTemplates] = useState<Template[]>(PLACEHOLDER_TEMPLATES);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const [siteName, setSiteName] = useState('');
  const [subdomain, setSubdomain] = useState('');
  const [isSubdomainAvailable, setIsSubdomainAvailable] = useState<boolean | null>(null);
  const [isCheckingSubdomain, setIsCheckingSubdomain] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  const filteredTemplates = selectedCategory === 'all'
    ? templates
    : templates.filter((t) => t.category === selectedCategory);

  // Check subdomain availability
  useEffect(() => {
    if (subdomain.length < 3) {
      setIsSubdomainAvailable(null);
      return;
    }

    const timer = setTimeout(async () => {
      setIsCheckingSubdomain(true);
      try {
        const available = await checkSubdomain(subdomain);
        setIsSubdomainAvailable(available);
      } catch {
        setIsSubdomainAvailable(null);
      } finally {
        setIsCheckingSubdomain(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [subdomain, checkSubdomain]);

  const handleSelectTemplate = (template: Template) => {
    setSelectedTemplate(template);
    setIsDialogOpen(true);
  };

  const handleCreateSite = async () => {
    if (!siteName || !subdomain || !isSubdomainAvailable) return;

    setIsCreating(true);
    try {
      const site = await createSite(siteName, subdomain, selectedTemplate?.id);
      toast({ title: 'Site créé !', description: 'Vous pouvez maintenant personnaliser votre site.' });
      navigate(`/builder/${site.id}`);
    } catch (error: any) {
      toast({
        title: 'Erreur',
        description: error.message || 'Impossible de créer le site.',
        variant: 'destructive',
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold">Choisissez un template</h1>
            <p className="text-sm text-muted-foreground">
              Sélectionnez un template et personnalisez-le selon vos besoins
            </p>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Categories */}
        <div className="flex gap-2 overflow-x-auto pb-4 mb-8">
          {CATEGORIES.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={cn(
                'px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors',
                selectedCategory === category.id
                  ? 'bg-primary text-white'
                  : 'bg-white border hover:border-primary'
              )}
            >
              {category.name}
            </button>
          ))}
        </div>

        {/* Templates grid */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {/* Blank template */}
          <Card
            className="cursor-pointer hover:shadow-lg transition-shadow group"
            onClick={() => handleSelectTemplate({ id: '', name: 'Page vide', description: '', category: '', thumbnail: '', isPremium: false })}
          >
            <CardContent className="p-0">
              <div className="aspect-[4/3] bg-gray-100 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-2">
                    <span className="text-2xl">+</span>
                  </div>
                  <p className="text-sm text-muted-foreground">Commencer de zéro</p>
                </div>
              </div>
              <div className="p-4">
                <h3 className="font-medium">Page vide</h3>
                <p className="text-sm text-muted-foreground">
                  Créez votre site de A à Z
                </p>
              </div>
            </CardContent>
          </Card>

          {filteredTemplates.map((template) => (
            <Card
              key={template.id}
              className="cursor-pointer hover:shadow-lg transition-shadow group overflow-hidden"
              onClick={() => handleSelectTemplate(template)}
            >
              <CardContent className="p-0">
                <div className="aspect-[4/3] relative overflow-hidden">
                  <img
                    src={template.thumbnail}
                    alt={template.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  {template.isPremium && (
                    <Badge className="absolute top-2 right-2" variant="warning">
                      <Sparkles className="w-3 h-3 mr-1" />
                      Premium
                    </Badge>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="font-medium">{template.name}</h3>
                  <p className="text-sm text-muted-foreground">
                    {template.description}
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Create site dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Créer votre site</DialogTitle>
            <DialogDescription>
              {selectedTemplate?.name
                ? `Template: ${selectedTemplate.name}`
                : 'Commencer avec une page vide'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="siteName">Nom du site</Label>
              <Input
                id="siteName"
                placeholder="Mon Super Site"
                value={siteName}
                onChange={(e) => setSiteName(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="subdomain">Adresse du site</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="subdomain"
                  placeholder="mon-site"
                  value={subdomain}
                  onChange={(e) => setSubdomain(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                  className={cn(
                    isSubdomainAvailable === false && 'border-destructive',
                    isSubdomainAvailable === true && 'border-green-500'
                  )}
                />
                <span className="text-sm text-muted-foreground whitespace-nowrap">.mawkaa.tn</span>
              </div>
              {subdomain.length >= 3 && (
                <p className={cn(
                  'text-xs',
                  isSubdomainAvailable === true && 'text-green-600',
                  isSubdomainAvailable === false && 'text-destructive'
                )}>
                  {isCheckingSubdomain
                    ? 'Vérification...'
                    : isSubdomainAvailable
                    ? 'Disponible !'
                    : 'Ce sous-domaine est déjà pris'}
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleCreateSite}
              disabled={!siteName || !subdomain || !isSubdomainAvailable || isCreating}
              loading={isCreating}
            >
              Créer le site
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
